/**
 * Miami's palm, in the shared 120×120 LINE asset box.
 *
 * The theme's hero motif (owner 2026-08-13), drawn from its own icon: a leaning
 * trunk under a crown of seven drooping fronds, which is the foreground of that
 * icon and the one thing everybody pictures when they hear Miami.
 *
 * NOT shared with Roraima's palm (line-palm-glyph). That one is a moriche fan
 * palm standing in a savanna; this is a coconut palm, and the silhouettes are
 * different. Putting both behind one module would mean tuning Miami's tree
 * silently redrew Roraima's — the drift [[line-shared-glyph-modules]] warns
 * about, running the other way.
 *
 * Generated rather than boundary-traced. The icon's palms overlap each other and
 * the ground as one connected silhouette, so a trace returns the cluster, not a
 * tree; and at 22px a faithful trace turns to mush anyway. The proportions are
 * measured off the icon, the construction is its own.
 */

const f = ( v: number ) => +v.toFixed( 1 );

/** Crown centre and trunk foot. The foot sits RIGHT of the crown so the trunk
 *  leans up and to the left, the way the owner's reference palm does. */
const CX = 54, CY = 44;
const BX = 70, BY = 112;

type P = [ number, number ];

/** A point on the frond's spine, and the spine's direction there. */
function spine( ang: number, len: number, droop: number ) {
  const a = ( ang * Math.PI ) / 180;
  const ux = Math.cos( a ), uy = Math.sin( a );
  const ex = CX + ux * len, ey = CY + uy * len + droop;
  const mx = CX + ux * len * 0.52, my = CY + uy * len * 0.52 - len * 0.18;
  const at = ( t: number ): P => {
    const u = 1 - t;
    return [ u * u * CX + 2 * u * t * mx + t * t * ex, u * u * CY + 2 * u * t * my + t * t * ey ];
  };
  return at;
}

/**
 * ONE frond, as a SINGLE SOLID BLADE.
 *
 * Rebuilt 2026-08-13 against the reference the owner supplied: a classic palm
 * silhouette where each frond is one smooth sickle, not a rachis carrying
 * leaflets. Every earlier round tried to simplify the leaflets and the answer
 * was that the leaflets themselves were the problem — a palm reads by the
 * SHAPE of its fronds, and at any size a serrated edge averages into fuzz.
 *
 * The blade sits on ONE side of a drooping spine, which is what makes it a
 * sickle rather than a leaf: the outer edge bellies out early and sweeps to a
 * point, the inner edge follows the spine back, and because the spine itself
 * curves, the inner edge comes out concave for free.
 */
function frond( ang: number, len: number, width: number, droop: number, side: number ): string {
  const at = spine( ang, len, droop );
  const N = 20;
  const outer: string[] = [], inner: string[] = [];

  for ( let i = 0; i <= N; i++ ) {
    const t = i / N;
    const [ x, y ] = at( t );
    const [ nx, ny ] = at( Math.min( 1, t + 0.01 ) );
    let dx = nx - x, dy = ny - y;
    const L = Math.hypot( dx, dy ) || 1;
    dx /= L; dy /= L;
    const px = -dy * side, py = dx * side;

    // The belly peaks around 60% out and runs to zero at both ends.
    //
    // The exponent is what keeps the five fronds SEPARATE (owner 2026-08-13:
    // "üst üste binen yaprakları kaldır, hepsi ayrı ayrı olsun"). They all
    // radiate from one point, so near the crown the neighbouring spines are only
    // a few px apart while a blade wants to be widest — that is where they
    // collided. Holding the width down until the spines have fanned out trades
    // nothing: the base of a real frond is a stalk anyway.
    const w = width * Math.sin( Math.PI * Math.pow( t, 1.35 ) );
    // A sliver of body on the inner side keeps the base from pinching to a
    // thread where five fronds meet.
    const wi = w * 0.07;

    outer.push( `${ f( x + px * w ) },${ f( y + py * w ) }` );
    inner.push( `${ f( x - px * wi ) },${ f( y - py * wi ) }` );
  }

  return `M${ outer[ 0 ] } L${ outer.slice( 1 ).join( ' L' ) } L${ inner.reverse().join( ' L' ) } Z`;
}

/**
 * angle, length, blade width, tip droop, belly side.
 *
 * FIVE fronds (owner 2026-08-13: "5 ana dal"), fanned from lower-left round to
 * lower-right. The outer pair droop hardest, which is what a coconut palm does
 * and a fan palm does not. `side` puts every belly on the upper edge: the
 * perpendicular flips sign as a frond crosses the vertical, so the fronds
 * leaning left take +1 and the ones leaning right take -1.
 *
 * THE ANGLES ARE NOT EVENLY SPACED, on purpose. Fronds 3 and 4 are the pair
 * whose bellies face EACH OTHER — every other neighbouring pair has one belly
 * turned away — so at even spacing they were the one collision in the crown
 * (owner: "sağda 2. ve 3. yapraklar hala çok yakın"). Measured closest approach
 * between neighbours, in the 120 box: 5.2 / 5.5 / 0.3 / 5.0 before, and
 * 5.4 / 4.3 / 3.8 / 3.4 now. Widening that one gap is what evens the crown out;
 * spacing them equally is what makes it look uneven.
 */
const FRONDS: [ number, number, number, number, number ][] = [
  [ 178, 56, 12, 34, 1 ],
  [ 218, 58, 13, 20, 1 ],
  [ 258, 50, 12, 5, 1 ],
  [ 318, 58, 13, 20, -1 ],
  [ 350, 56, 12, 34, -1 ],
];

/** The whole crown, as one path. */
export const MIAMI_PALM_CROWN: string = FRONDS.map( a => frond( ...a ) ).join( ' ' );

/* The coconut cluster and the trunk segment rings used to be here. Both were
 * removed 2026-08-13 ("yapraklar dışında detay olmasın") — at a 120px asset
 * scaled to roughly 30px on the phone, neither survived, and the nuts in
 * particular put a dark blob in the middle of the crown. */

function trunkSpine() {
  const c: P = [ BX + 2, ( BY + CY ) / 2 ];
  return ( t: number ): P => {
    const u = 1 - t;
    return [ u * u * BX + 2 * u * t * c[ 0 ] + t * t * CX, u * u * BY + 2 * u * t * c[ 1 ] + t * t * CY ];
  };
}

/** The trunk: tapered and bowed, thick enough to still read at tab size. */
export const MIAMI_PALM_TRUNK: string = ( () => {
  const at = trunkSpine();
  const N = 14;
  const top: string[] = [], bot: string[] = [];
  for ( let i = 0; i <= N; i++ ) {
    const t = i / N;
    const [ x, y ] = at( t );
    const [ nx, ny ] = at( Math.min( 1, t + 0.01 ) );
    let dx = nx - x, dy = ny - y;
    const L = Math.hypot( dx, dy ) || 1;
    dx /= L; dy /= L;
    const w = 6.2 - 3.4 * t;
    top.push( `${ f( x - dy * w ) },${ f( y + dx * w ) }` );
    bot.push( `${ f( x + dy * w ) },${ f( y - dx * w ) }` );
  }
  return `M${ top[ 0 ] } L${ top.slice( 1 ).join( ' L' ) } L${ bot.reverse().join( ' L' ) } Z`;
} )();


/**
 * The per-digit leans the passcode applies, plus the un-rotated Home tab. The
 * fit below has to hold for ALL of them, so it is measured over the set.
 */
export const MIAMI_PALM_ROTATIONS = [ 0, -9, 7, -6, 10 ];

/** The pivot the lean turns about: the trunk's foot, so the crown swings and
 *  the base stays planted. */
export const MIAMI_PALM_PIVOT: P = [ 60, 96 ];

/**
 * Fit the tree into the 120 box — computed, not guessed.
 *
 * The crown ran off the TOP of the viewBox and was being clipped (owner
 * 2026-08-13: "kesildi"). It was already marginal, and thickening every stroke
 * pushed it clearly over. Nudging a constant until it looked right would only
 * hold until the next boldness change, so the extent is measured instead: every
 * vertex of the crown and trunk, rotated through each lean, reduced to one
 * bounding box, then scaled and centred to sit inside the box with a margin.
 *
 * Exact rather than approximate — `frond()` and the trunk emit only M/L/Z, so
 * the vertices ARE the outline. (There is nothing else left in the tree to measure.)
 */
const PAD = 5;

export const MIAMI_PALM_FIT: string = ( () => {
  const pts: P[] = [];
  const re = /(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/g;
  for ( const d of [ MIAMI_PALM_CROWN, MIAMI_PALM_TRUNK ] ) {
    let m: RegExpExecArray | null;
    while ( ( m = re.exec( d ) ) !== null ) { pts.push( [ +m[ 1 ], +m[ 2 ] ] ); }
  }

  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  const [ px, py ] = MIAMI_PALM_PIVOT;
  for ( const deg of MIAMI_PALM_ROTATIONS ) {
    const a = ( deg * Math.PI ) / 180, c = Math.cos( a ), s2 = Math.sin( a );
    for ( const [ x, y ] of pts ) {
      const dx = x - px, dy = y - py;
      const rx = px + dx * c - dy * s2, ry = py + dx * s2 + dy * c;
      if ( rx < x0 ) { x0 = rx; } if ( rx > x1 ) { x1 = rx; }
      if ( ry < y0 ) { y0 = ry; } if ( ry > y1 ) { y1 = ry; }
    }
  }

  const span = 120 - PAD * 2;
  const k = Math.min( span / ( x1 - x0 ), span / ( y1 - y0 ), 1 );
  // Centre what is left over, so shrinking never shoves the tree into a corner.
  const tx = PAD + ( span - ( x1 - x0 ) * k ) / 2 - x0 * k;
  const ty = PAD + ( span - ( y1 - y0 ) * k ) / 2 - y0 * k;
  return `translate(${ f( tx ) },${ f( ty ) }) scale(${ +k.toFixed( 4 ) })`;
} )();

/**
 * Standalone fronds, for the chat wallpaper.
 *
 * The family rule: the theme's HERO stays whole on the tab and the passcode, and
 * a FRAGMENT of it drifts across the chat background. Sakura's blossom sheds
 * petals, Autumn's maple sheds leaves, Roraima's tepui sends up gulls. Miami's
 * palm sheds fronds — which is also the one Miami fragment that floats without
 * implying a horizon, the trap that sank the flying-mountain and hovering-ferry
 * ideas.
 *
 * Generated at angle 0 so each blade points along +x and can be rotated freely
 * by the scatter. They are anchored at the crown origin, so a caller finishes
 * its transform with `translate(-54,-44)`.
 */
export const MIAMI_FROND_ANCHOR: P = [ CX, CY ];

export const MIAMI_FRONDS: string[] = [
  frond( 0, 60, 13, 10, 1 ),
  frond( 0, 52, 11, 17, -1 ),
  frond( 0, 66, 14, 5, 1 ),
  frond( 0, 48, 12, 21, -1 ),
];

/**
 * How far each frond actually reaches from its anchor, measured off the path it
 * generated rather than guessed from the length parameter (the belly and the
 * droop both push past it).
 *
 * The chat wallpaper needs this to place fronds without overlapping: a blade is
 * long and thin, so a scatter that only knows a nominal size either collides or
 * leaves the canvas half empty.
 */
export const MIAMI_FROND_REACH: number[] = MIAMI_FRONDS.map( d => {
  let max = 0;
  const re = /(-?\d+(?:\.\d+)?),(-?\d+(?:\.\d+)?)/g;
  let m: RegExpExecArray | null;
  while ( ( m = re.exec( d ) ) !== null ) {
    max = Math.max( max, Math.hypot( +m[ 1 ] - CX, +m[ 2 ] - CY ) );
  }
  return +max.toFixed( 1 );
} );

/**
 * The trunk ink: Neon Raspberry, the palette's own dark pink (owner 2026-08-13:
 * "gövde koyu pembe").
 *
 * ONE value doing both states, which is what makes the pair read as one tree:
 * unentered, the WHOLE palm is this pink; entered, the trunk stays exactly this
 * pink and only the crown lights up aqua. Nothing moves except the light.
 *
 * Measured on the surface these dots actually sit on — .lk-topzone, the skin's
 * hot pink #FC5B96 — where it holds 2.63:1, and 4.05:1 against the lit crown so
 * trunk and canopy stay separate. The muted mauve this replaced (#B17F92, the
 * idle TAB tone) measured 1.12:1 on that band: two mid pinks, all but invisible.
 * It was picked against the pale #FFECF3 further down the screen, which is not
 * where these dots are.
 */
export const MIAMI_PALM_INK = '#99224D';



// The LINE CHROME colours are not set here. They are pixel-sampled from the
// owner's device screenshots and live in line-color-set.ts.
